// src/features/secret/TypedConfirmDialog.tsx
// Destructive-action confirmation requiring the user to type an exact phrase (a mount path, a
// serial number, ...) before the confirm button enables. Local to features/secret — not a
// shared-component edit; src/components/ConfirmDialog.tsx has no typed-confirmation mode.

import { useEffect, useState, type ReactNode } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Input } from '@react/components';

export interface TypedConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  /** The exact string the user must type to enable the confirm button. */
  phrase: string;
  confirmLabel?: string;
  isConfirming?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Extra body content composed above the typed-phrase prompt (e.g. a revoke-reason field) —
   * lets callers extend this dialog instead of hand-rolling their own typed-confirm UI. */
  children?: ReactNode;
}

export function TypedConfirmDialog({
  open, title, description, phrase, confirmLabel, isConfirming, onConfirm, onCancel, children,
}: TypedConfirmDialogProps) {
  const { t } = useI18n();
  const [typed, setTyped] = useState('');

  useEffect(() => { if (open) setTyped(''); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const matches = typed === phrase;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onCancel} aria-hidden="true" />
      <div role="dialog" aria-modal="true" className="relative mx-4 w-full max-w-md rounded-lg border border-destructive/50 bg-background p-5 shadow-xl">
        <h3 className="text-base font-semibold text-destructive">{title}</h3>
        {description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}
        {children && <div className="mt-3">{children}</div>}
        <p className="mt-3 text-sm text-muted-foreground">{t('common.typedConfirmPrompt', { phrase })}</p>
        <code className="mt-1 block rounded bg-muted px-2 py-1 text-sm font-mono text-foreground select-all">{phrase}</code>
        <Input className="mt-3" value={typed} onChange={(e) => setTyped(e.target.value)} aria-label={t('common.typedConfirmInputLabel')} />
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={isConfirming} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50">
            {t('common.cancel')}
          </button>
          <Button size="sm" variant="danger" onClick={onConfirm} isLoading={isConfirming} disabled={!matches}>
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
